import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Card, CardContent, Typography, TextField, Button, Grid, Box,
  IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Snackbar, Alert, Dialog, DialogActions, DialogContent, DialogTitle,
  InputAdornment, Chip, FormControl, InputLabel, Select, MenuItem, CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LinkIcon from '@mui/icons-material/Link';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import { Document, Page, pdfjs } from 'react-pdf';
import api from '../../services/api';
import { normalizeSubscriberDocument, isValidSubscriberDocument } from '../../utils/document';

const EXPENSE_SUGGESTIONS = [
  'Combustível',
  'Transporte Diversos',
  'Despesas Administrativas',
  'Alimentação',
  'Hospedagens'
];

const STATUS_OPTIONS = ['Rascunho', 'Pendente', 'Aprovado', 'Recusado', 'Pago'];

const formatBRL = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const number = Number(value);
  if (isNaN(number)) return '';
  return number.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const parseBRL = (value) => {
  if (!value) return 0;
  const cleaned = String(value).replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.');
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

const maskCurrency = (value) => {
  const digits = String(value).replace(/\D/g, '');
  const number = parseInt(digits || '0', 10) / 100;
  return number.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });
};

const getReceiptUrl = (proposalId, expenseId) => {
  if (!proposalId || !expenseId) return null;
  return `${api.defaults.baseURL}/reimbursement-proposals/${proposalId}/receipt/${expenseId}`;
};

const isImage = (mime) => mime?.startsWith('image/');
const isPdf = (mime) => mime === 'application/pdf';

const emptyExpense = () => ({
  id: `${Date.now()}-${Math.random()}`,
  description: '',
  amount: '',
  receipt: null
});

const emptyProposal = () => ({
  id: null,
  description: '',
  proponent_name: '',
  proponent_role: '',
  proponent_document: '',
  status: 'Rascunho',
  expenses: [emptyExpense()]
});

function PrintablePdf({ url }) {
  const [numPages, setNumPages] = useState(null);

  return (
    <Document
      file={url}
      onLoadSuccess={({ numPages: loadedPages }) => setNumPages(loadedPages)}
      loading={<Typography color="text.secondary">Carregando PDF...</Typography>}
      error={<Typography color="error">Não foi possível carregar este PDF.</Typography>}
    >
      {Array.from({ length: numPages || 0 }, (_, index) => (
        <Box key={`pdf-page-${index + 1}`} className="print-report-pdf-page">
          <Page
            pageNumber={index + 1}
            width={640}
            renderAnnotationLayer={false}
            renderTextLayer={false}
          />
        </Box>
      ))}
    </Document>
  );
}

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export default function PrestacaoContas() {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProposal());
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [preview, setPreview] = useState({ open: false, url: null, mime: null, name: '' });
  const [linkDialog, setLinkDialog] = useState({ open: false, url: '' });
  const [printProposal, setPrintProposal] = useState(null);
  const [loadingPrintReport, setLoadingPrintReport] = useState(false);
  const [creatingPublicLink, setCreatingPublicLink] = useState(false);
  const [cpfLoading, setCpfLoading] = useState(false);
  const [cpfError, setCpfError] = useState('');
  const [cpfValidated, setCpfValidated] = useState(false);

  const loadUser = useCallback(async () => {
    try {
      const { data } = await api.get('/users/me?populate=*');
      setForm(prev => ({
        ...prev,
        proponent_name: data.name || data.username || '',
        proponent_document: data.document || data.cpf || ''
      }));
    } catch (err) {
      console.error('Erro ao carregar usuário:', err);
    }
  }, []);

  const loadProposals = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/reimbursement-proposals?populate[expenses][populate]=receipt&sort=createdAt:desc');
      setProposals(data.data || []);
    } catch (err) {
      setError('Erro ao carregar propostas: ' + (err.response?.data?.error?.message || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
    loadProposals();
  }, [loadUser, loadProposals]);

  const handleOpenForm = (proposal = null) => {
    setCpfValidated(false);
    setCpfError('');
    if (proposal) {
      const attributes = proposal.attributes || proposal;
      setEditing(proposal.id);
      setForm({
        id: proposal.id,
        description: attributes.description || '',
        proponent_name: attributes.proponent_name || '',
        proponent_role: attributes.proponent_role || '',
        proponent_document: attributes.proponent_document || '',
        status: attributes.status || 'Rascunho',
        expenses: (attributes.expenses || []).map((exp, idx) => ({
          id: exp.id || `${Date.now()}-${idx}`,
          description: exp.description || '',
          amount: exp.amount !== null && exp.amount !== undefined ? formatBRL(exp.amount) : '',
          receipt: exp.receipt?.data || exp.receipt || null
        }))
      });
    } else {
      setEditing(null);
      setForm(emptyProposal());
      loadUser();
    }
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditing(null);
    setForm(emptyProposal());
    setCpfValidated(false);
    setCpfError('');
  };

  const handleAddExpense = () => {
    setForm(prev => ({ ...prev, expenses: [...prev.expenses, emptyExpense()] }));
  };

  const handleAddExpenseSuggestion = (description) => {
    setForm(prev => {
      const hasOnlyEmptyExpense = prev.expenses.length === 1
        && !prev.expenses[0].description
        && !prev.expenses[0].amount
        && !prev.expenses[0].receipt;

      if (hasOnlyEmptyExpense) {
        return { ...prev, expenses: [{ ...prev.expenses[0], description }] };
      }

      return { ...prev, expenses: [...prev.expenses, { ...emptyExpense(), description }] };
    });
  };

  const handleCpfLookup = async () => {
    const document = normalizeSubscriberDocument(form.proponent_document);
    if (!isValidSubscriberDocument(document)) {
      setCpfError('Digite um CPF válido ou passaporte para buscar');
      return;
    }

    setCpfLoading(true);
    setCpfError('');
    try {
      const { data } = await api.post('/subscriber/form', { data: { cpf: document } });
      if (!data) {
        setCpfValidated(false);
        setCpfError('Nenhum inscrito encontrado para este documento');
        return;
      }

      setForm(prev => ({
        ...prev,
        proponent_name: data.name || '',
        proponent_role: data.office || data.role || ''
      }));
      setCpfValidated(true);
    } catch (err) {
      setCpfValidated(false);
      setCpfError(err.response?.data?.error?.message || 'Não foi possível localizar este documento');
    } finally {
      setCpfLoading(false);
    }
  };

  const handleRemoveExpense = (expenseId) => {
    setForm(prev => ({
      ...prev,
      expenses: prev.expenses.filter(e => e.id !== expenseId)
    }));
  };

  const handleExpenseChange = (expenseId, field, value) => {
    setForm(prev => ({
      ...prev,
      expenses: prev.expenses.map(e => {
        if (e.id !== expenseId) return e;
        if (field === 'amount') {
          return { ...e, [field]: maskCurrency(value) };
        }
        return { ...e, [field]: value };
      })
    }));
  };

  const handleFileChange = async (expenseId, file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('files', file);
    formData.append('private', 'true');
    formData.append('folderPath', 'receipts/private');

    try {
      const { data } = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const uploaded = Array.isArray(data) ? data[0] : data;
      handleExpenseChange(expenseId, 'receipt', uploaded);
      setSuccess('Comprovante enviado com sucesso');
    } catch (err) {
      setError('Erro ao enviar comprovante: ' + (err.response?.data?.error?.message || err.message));
    }
  };

  const getMimeFromResponse = (response, fallbackMime) => {
    const contentType = response?.headers?.['content-type'];
    if (contentType) {
      return contentType.split(';')[0].trim();
    }
    return fallbackMime || 'application/octet-stream';
  };

  const handlePreview = async (proposalId, expenseId, fileName, mime) => {
    try {
      const url = getReceiptUrl(proposalId, expenseId);
      const response = await api.get(url, { responseType: 'blob' });
      const detectedMime = getMimeFromResponse(response, mime);
      const blob = new Blob([response.data], { type: detectedMime });
      const blobUrl = URL.createObjectURL(blob);
      setPreview({ open: true, url: blobUrl, mime: detectedMime, name: fileName });
    } catch (err) {
      setError('Erro ao carregar comprovante: ' + (err.response?.data?.error?.message || err.message));
    }
  };

  const handleClosePreview = () => {
    if (preview.url) URL.revokeObjectURL(preview.url);
    setPreview({ open: false, url: null, mime: null, name: '' });
  };

  const buildPublicUrl = (token) => {
    const prefix = window.location.pathname.split('/').filter(Boolean)[0];
    return `${window.location.origin}/${prefix}/prestacao-contas/publico/${token}`;
  };

  const handleGenerateLink = async (item) => {
    const attr = item.attributes || item;
    try {
      let token = attr.public_token;
      if (!token) {
        token = crypto.randomUUID();
        await api.put(`/reimbursement-proposals/${item.id}`, { data: { public_token: token } });
        loadProposals();
      }
      setLinkDialog({ open: true, url: buildPublicUrl(token) });
    } catch (err) {
      setError('Erro ao gerar link público: ' + (err.response?.data?.error?.message || err.message));
    }
  };

  const handleCreatePublicLink = async () => {
    if (creatingPublicLink) return;
    setCreatingPublicLink(true);
    try {
      const { data } = await api.post('/reimbursement-proposals/public-link');
      const token = data?.data?.attributes?.public_token || data?.data?.public_token;
      if (!token) throw new Error('Token público não retornado pela API');
      await loadProposals();
      setLinkDialog({ open: true, url: buildPublicUrl(token) });
    } catch (err) {
      setError('Erro ao criar link público: ' + (err.response?.data?.error?.message || err.message));
    } finally {
      setCreatingPublicLink(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(linkDialog.url);
      setSuccess('Link copiado para a área de transferência');
    } catch (err) {
      setError('Não foi possível copiar o link');
    }
  };

  const handleCloseLinkDialog = () => setLinkDialog({ open: false, url: '' });

  const handlePrintProposal = async (proposal) => {
    const attributes = proposal.attributes || proposal;
    const expenses = attributes.expenses || [];
    setLoadingPrintReport(true);
    setPrintProposal({
      ...attributes,
      id: proposal.id,
      expenses: expenses.map(expense => ({ ...expense, attachmentUrl: null, attachmentMime: null }))
    });

    try {
      const expensesWithAttachments = await Promise.all(expenses.map(async (expense) => {
        const receipt = expense.receipt?.data || expense.receipt;
        if (!receipt?.id || !proposal.id || !expense.id) return expense;

        try {
          const response = await api.get(getReceiptUrl(proposal.id, expense.id), { responseType: 'blob' });
          const mime = response.headers?.['content-type']?.split(';')[0].trim() || receipt.mime || 'application/octet-stream';
          return {
            ...expense,
            attachmentUrl: URL.createObjectURL(new Blob([response.data], { type: mime })),
            attachmentMime: mime
          };
        } catch (error) {
          return { ...expense, attachmentError: true };
        }
      }));

      setPrintProposal(prev => prev ? { ...prev, expenses: expensesWithAttachments } : prev);
    } finally {
      setLoadingPrintReport(false);
    }
  };

  const handleClosePrintProposal = () => {
    printProposal?.expenses?.forEach(expense => {
      if (expense.attachmentUrl) URL.revokeObjectURL(expense.attachmentUrl);
    });
    setPrintProposal(null);
  };

  const handlePrint = () => {
    window.print();
  };

  const totalAmount = useMemo(() => {
    return form.expenses.reduce((sum, exp) => sum + parseBRL(exp.amount), 0);
  }, [form.expenses]);

  const buildPayload = () => ({
    description: form.description,
    proponent_name: form.proponent_name,
    proponent_role: form.proponent_role,
    proponent_document: form.proponent_document,
    status: form.status,
    expenses: form.expenses.map(exp => ({
      id: typeof exp.id === 'number' ? exp.id : undefined,
      description: exp.description,
      amount: parseBRL(exp.amount),
      receipt: exp.receipt?.id || null
    }))
  });

  const handleSave = async () => {
    if (!cpfValidated || !form.proponent_name || !form.proponent_role || !form.proponent_document) {
      setError('Informe o CPF ou passaporte e faça a busca antes de salvar a proposta');
      return;
    }

    if (form.expenses.length === 0 || form.expenses.some(e => !e.description || !e.amount)) {
      setError('Preencha descrição e valor de todas as despesas');
      return;
    }

    try {
      const payload = buildPayload();
      if (editing) {
        await api.put(`/reimbursement-proposals/${editing}`, { data: payload });
        setSuccess('Proposta atualizada com sucesso');
      } else {
        await api.post('/reimbursement-proposals', { data: payload });
        setSuccess('Proposta criada com sucesso');
      }
      handleCloseForm();
      loadProposals();
    } catch (err) {
      setError('Erro ao salvar proposta: ' + (err.response?.data?.error?.message || err.message));
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/reimbursement-proposals/${id}`);
      setSuccess('Proposta excluída com sucesso');
      setDeleteDialog(null);
      loadProposals();
    } catch (err) {
      setError('Erro ao excluir proposta: ' + (err.response?.data?.error?.message || err.message));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Rascunho': return 'default';
      case 'Pendente': return 'warning';
      case 'Aprovado': return 'info';
      case 'Pago': return 'success';
      case 'Recusado': return 'error';
      default: return 'default';
    }
  };

  return (
    <Card sx={{ margin: 'auto', mt: 5, mx: 4 }}>
      <CardContent>
        <style>{`
          @media print {
            body * { visibility: hidden !important; }
            #printable-reimbursement-report,
            #printable-reimbursement-report * { visibility: visible !important; }
            #printable-reimbursement-report {
              position: absolute;
              left: 0;
              top: 0;
              width: 210mm;
              min-height: 297mm;
              box-sizing: border-box;
              padding: 16mm;
              background: white;
            }
            .print-report-title,
            .print-report-actions { display: none !important; }
            .print-report-expense { break-inside: avoid; page-break-inside: avoid; }
            .print-report-attachment { max-width: 100%; max-height: 240mm; object-fit: contain; }
            .print-report-pdf-page { break-after: page; page-break-after: always; }
            .print-report-pdf-page:last-child { break-after: auto; page-break-after: auto; }
            @page { size: A4; margin: 0; }
          }
        `}</style>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Prestação de Contas</Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenForm()}
          >
            Nova Proposta
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={creatingPublicLink ? <CircularProgress size={18} /> : <LinkIcon />}
            onClick={handleCreatePublicLink}
            disabled={creatingPublicLink}
          >
            Criar Link Público
          </Button>
        </Box>

        {proposals.length === 0 && !loading && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Nenhuma proposta de reembolso encontrada.
          </Typography>
        )}

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nº</TableCell>
                <TableCell>Proponente</TableCell>
                <TableCell>Cargo</TableCell>
                <TableCell>Documento</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {proposals.map((item) => {
                const attr = item.attributes || item;
                return (
                  <TableRow key={item.id}>
                    <TableCell>{attr.proposal_number}</TableCell>
                    <TableCell>{attr.proponent_name}</TableCell>
                    <TableCell>{attr.proponent_role}</TableCell>
                    <TableCell>{attr.proponent_document}</TableCell>
                    <TableCell>{formatBRL(attr.total_amount)}</TableCell>
                    <TableCell>
                      <Chip label={attr.status} color={getStatusColor(attr.status)} size="small" />
                    </TableCell>
                    <TableCell align="right">
                      <>
                        {attr.status === 'Rascunho' && (
                          <IconButton color="info" onClick={() => handleGenerateLink(item)} title="Gerar/copiar link público">
                            <LinkIcon />
                          </IconButton>
                        )}
                        <IconButton color="primary" onClick={() => handleOpenForm(item)} title="Editar">
                          <EditIcon />
                        </IconButton>
                        <IconButton color="secondary" onClick={() => handlePrintProposal(item)} title="Imprimir relatório">
                          <PrintIcon />
                        </IconButton>
                        {attr.status === 'Rascunho' && (
                          <IconButton color="error" onClick={() => setDeleteDialog(item)} title="Excluir">
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog open={formOpen} onClose={handleCloseForm} maxWidth="lg" fullWidth>
          <DialogTitle>{editing ? 'Editar Proposta' : 'Nova Proposta de Reembolso'}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="CPF ou passaporte do Proponente"
                  value={form.proponent_document}
                  onChange={(e) => {
                    setCpfValidated(false);
                    setCpfError('');
                    setForm(prev => ({ ...prev, proponent_document: normalizeSubscriberDocument(e.target.value), proponent_name: '', proponent_role: '' }));
                  }}
                  error={Boolean(cpfError)}
                  helperText={cpfError || 'Informe o CPF ou passaporte e faça a busca para carregar nome e cargo'}
                  inputProps={{ maxLength: 20 }}
                  disabled={cpfLoading}
                />
                <Button
                  variant="contained"
                  onClick={handleCpfLookup}
                  disabled={cpfLoading || !isValidSubscriberDocument(form.proponent_document)}
                  startIcon={cpfLoading ? <CircularProgress size={18} /> : <SearchIcon />}
                  sx={{ mt: 1 }}
                >
                  Buscar documento
                </Button>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nome do Proponente"
                  value={form.proponent_name}
                  InputProps={{ readOnly: true }}
                  disabled={!cpfValidated}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Cargo"
                  value={form.proponent_role}
                  InputProps={{ readOnly: true }}
                  disabled={!cpfValidated}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel id="status-label">Status</InputLabel>
                  <Select
                    labelId="status-label"
                    value={form.status}
                    label="Status"
                    onChange={(e) => setForm(prev => ({ ...prev, status: e.target.value }))}
                  >
                    {STATUS_OPTIONS.map(s => (
                      <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Descrição Livre"
                  value={form.description}
                  onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>Despesas</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {EXPENSE_SUGGESTIONS.map((suggestion, index) => (
                <Button
                  key={suggestion}
                  size="small"
                  variant="outlined"
                  onClick={() => handleAddExpenseSuggestion(suggestion)}
                >
                  {index === 0 ? suggestion : `${index} ${suggestion}`}
                </Button>
              ))}
            </Box>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Descrição</TableCell>
                    <TableCell width="180px">Valor (R$)</TableCell>
                    <TableCell width="280px">Comprovante</TableCell>
                    <TableCell width="80px" align="center"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {form.expenses.map((exp) => (
                    <TableRow key={exp.id}>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          value={exp.description}
                          onChange={(e) => handleExpenseChange(exp.id, 'description', e.target.value)}
                          placeholder="Descrição da despesa"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          value={exp.amount}
                          onChange={(e) => handleExpenseChange(exp.id, 'amount', e.target.value)}
                          placeholder="0,00"
                          InputProps={{
                            startAdornment: <InputAdornment position="start"></InputAdornment>
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Button
                            variant="outlined"
                            component="label"
                            size="small"
                            startIcon={<AttachFileIcon />}
                          >
                            {exp.receipt ? 'Trocar' : 'Anexar'}
                            <input
                              type="file"
                              hidden
                              accept=".pdf,.jpg,.jpeg,.png"
                              onChange={(e) => handleFileChange(exp.id, e.target.files[0])}
                            />
                          </Button>
                          {exp.receipt && (
                            <>
                              <Typography variant="caption" noWrap sx={{ maxWidth: 100 }}>
                                {exp.receipt.name || 'Comprovante'}
                              </Typography>
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => handlePreview(form.id, typeof exp.id === 'number' ? exp.id : null, exp.receipt.name || 'Comprovante', exp.receipt.mime)}
                                title="Visualizar comprovante"
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton color="error" onClick={() => handleRemoveExpense(exp.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Button
              variant="text"
              startIcon={<AddIcon />}
              onClick={handleAddExpense}
              sx={{ mt: 1 }}
            >
              Adicionar Despesa
            </Button>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Typography variant="h6">
                Total: {formatBRL(totalAmount)}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseForm} startIcon={<CancelIcon />}>Cancelar</Button>
            <Button onClick={handleSave} variant="contained" color="success" startIcon={<SaveIcon />}>
              Salvar
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)}>
          <DialogTitle>Excluir proposta?</DialogTitle>
          <DialogContent>
            Deseja excluir a proposta nº {deleteDialog?.attributes?.proposal_number || deleteDialog?.proposal_number}?
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog(null)}>Cancelar</Button>
            <Button
              color="error"
              variant="contained"
              onClick={() => handleDelete(deleteDialog.id)}
            >
              Excluir
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={linkDialog.open} onClose={handleCloseLinkDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Link público da prestação de contas</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Compartilhe este link com o proponente. Ele poderá preencher a prestação de contas e anexar
              os comprovantes enquanto o status estiver como "Rascunho". Ao enviar, o status muda para
              "Pendente" e o link é bloqueado automaticamente.
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={linkDialog.url}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleCopyLink} title="Copiar link">
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseLinkDialog}>Fechar</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={Boolean(printProposal)} onClose={handleClosePrintProposal} maxWidth="md" fullWidth>
          <DialogTitle className="print-report-title">Relatório da Prestação de Contas</DialogTitle>
          <DialogContent>
            {printProposal && (
              <Box id="printable-reimbursement-report" sx={{ p: 1 }}>
                <Typography variant="h5" align="center" sx={{ mb: 3 }}>
                  Prestação de Contas
                </Typography>
                <Grid container spacing={1} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6}><Typography><strong>Nº da proposta:</strong> {printProposal.proposal_number || '-'}</Typography></Grid>
                  <Grid item xs={12} sm={6}><Typography><strong>Status:</strong> {printProposal.status || '-'}</Typography></Grid>
                  <Grid item xs={12} sm={6}><Typography><strong>Proponente:</strong> {printProposal.proponent_name || '-'}</Typography></Grid>
                  <Grid item xs={12} sm={6}><Typography><strong>Cargo:</strong> {printProposal.proponent_role || '-'}</Typography></Grid>
                  <Grid item xs={12} sm={6}><Typography><strong>Documento:</strong> {printProposal.proponent_document || '-'}</Typography></Grid>
                  <Grid item xs={12}><Typography><strong>Descrição:</strong> {printProposal.description || '-'}</Typography></Grid>
                </Grid>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Despesa</strong></TableCell>
                        <TableCell><strong>Valor</strong></TableCell>
                        <TableCell><strong>Comprovante</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {printProposal.expenses.length > 0 ? printProposal.expenses.map((expense, index) => {
                        const receipt = expense.receipt?.data || expense.receipt;
                        return (
                          <TableRow key={expense.id || index} className="print-report-expense">
                            <TableCell>{expense.description || '-'}</TableCell>
                            <TableCell>{formatBRL(expense.amount)}</TableCell>
                            <TableCell>{receipt?.name || (receipt ? 'Anexado' : 'Não anexado')}</TableCell>
                          </TableRow>
                        );
                      }) : (
                        <TableRow><TableCell colSpan={3} align="center">Nenhuma despesa cadastrada.</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Typography variant="h6">Total: {formatBRL(printProposal.total_amount)}</Typography>
                </Box>
                {printProposal.expenses.map((expense, index) => {
                  const receipt = expense.receipt?.data || expense.receipt;
                  return (
                    <Box key={`attachment-${expense.id || index}`} className="print-report-expense" sx={{ mt: 3, pt: 2, borderTop: '1px solid #ccc' }}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Comprovante: {expense.description || `Despesa ${index + 1}`}
                      </Typography>
                      {expense.attachmentUrl && expense.attachmentMime?.startsWith('image/') && (
                        <Box component="img" src={expense.attachmentUrl} alt={receipt?.name || 'Comprovante'} className="print-report-attachment" />
                      )}
                      {expense.attachmentUrl && expense.attachmentMime === 'application/pdf' && (
                        <Box className="print-report-attachment">
                          <PrintablePdf url={expense.attachmentUrl} />
                        </Box>
                      )}
                      {expense.attachmentError && (
                        <Typography color="error">Não foi possível carregar este comprovante.</Typography>
                      )}
                      {!expense.attachmentUrl && !expense.attachmentError && (
                        <Typography color="text.secondary">Comprovante não disponível.</Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </DialogContent>
          <DialogActions className="print-report-actions">
            <Button onClick={handleClosePrintProposal}>Fechar</Button>
            <Button onClick={handlePrint} variant="contained" startIcon={<PrintIcon />} disabled={loadingPrintReport}>
              {loadingPrintReport ? 'Carregando anexos...' : 'Imprimir'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={preview.open} onClose={handleClosePreview} maxWidth="lg" fullWidth>
          <DialogTitle>{preview.name}</DialogTitle>
          <DialogContent sx={{ minHeight: '60vh', display: 'flex', flexDirection: 'column' }}>
            {preview.url && isImage(preview.mime) && (
              <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'auto' }}>
                <img
                  src={preview.url}
                  alt={preview.name}
                  style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain' }}
                />
              </Box>
            )}
            {preview.url && isPdf(preview.mime) && (
              <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'auto' }}>
                <iframe
                  src={preview.url}
                  title={preview.name}
                  width="100%"
                  height="575vh"
                  style={{ border: 'none' }}
                />
              </Box>
            )}
            {preview.url && !isImage(preview.mime) && !isPdf(preview.mime) && (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography>Este tipo de arquivo não pode ser pré-visualizado. Faça o download para visualizar.</Typography>
              </Box>
            )}
            {!preview.url && (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClosePreview}>Fechar</Button>
            {preview.url && (
              <Button
                component="a"
                href={preview.url}
                download={preview.name}
                color="primary"
              >
                Download
              </Button>
            )}
          </DialogActions>
        </Dialog>

        <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
        </Snackbar>
        <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>
        </Snackbar>
      </CardContent>
    </Card>
  );
}
